import React from 'react';
import styled, { css } from 'styled-components';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import type { IFormCheckbox } from './types';

import { Checkmark } from '@/assets/icons';
import { sizing } from '@/styles/theme';
import { colors, otherColors } from '@/styles/colors';
import { InputDescription } from './components';

const Checkbox = (props: IFormCheckbox): React.ReactElement => {
  const {
    showTooltip = false,
    children,
    id,
    name,
    checked,
    defaultChecked,
    label,
    register,
    error,
    touched,
    value,
    type = 'checkbox',
    isReversed,
    className,
    isStandalone = true,
    ...rest
  } = props;

  const registerData = register && register(name);

  const realId = id || name;

  return (
    <Fieldset isReversed={isReversed} isStandalone={isStandalone} className={className}>
      {children}
      <InputField
        className={type === 'checkbox' ? 'checkbox' : ''}
        type={type}
        checked={checked}
        defaultChecked={defaultChecked}
        id={realId}
        name={name}
        value={value}
        {...registerData}
        {...rest}
      />
      {showTooltip && !!label ? (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="auto-end"
          overlay={<StyledTooltip id={realId}>{label}</StyledTooltip>}
        >
          <InputLabel htmlFor={realId}>{label}</InputLabel>
        </OverlayTrigger>
      ) : (
        <InputLabel htmlFor={realId}>{label}</InputLabel>
      )}
      {rest.description && <InputDescription>{rest.description}</InputDescription>}
      {/* TODO: generic error message for all checkboxes */}
      {error && <ErrorMessage>{(error && touched) || error?.type === 'api' ? error.message : ''}</ErrorMessage>}
    </Fieldset>
  );
};

export { Checkbox };

const Fieldset = styled.fieldset<{ isReversed?: boolean; isStandalone?: boolean }>`
  font-size: ${sizing.fontSize.sm};
  display: flex;
  align-items: center;
  margin-top: 0;

  ${({ isStandalone }) =>
    !isStandalone &&
    css`
      margin-bottom: 24px;
    `}

  ${({ isReversed }) =>
    isReversed &&
    css`
      flex-direction: row-reverse !important;
      justify-content: flex-end;
    `}
`;

const InputLabel = styled.label.attrs({
  className: 'm-0 mx-2',
})`
  font-size: 16px;
`;

const InputField = styled.input`
  cursor: pointer;
  min-width: 16px;
  min-height: 16px;
  border-radius: 4px;
  border: 1px solid ${props => props.color || 'gray'};
  background-color: #fff;
  position: relative;

  &.checkbox {
    appearance: none;

    &:checked {
      background-color: ${props => props.color || otherColors.lxc.blue};
      border-color: ${props => props.color || otherColors.lxc.blue};

      &::before {
        content: '';
        background-image: url(${Checkmark});
        background-position: 2px 3px;
        background-repeat: no-repeat;
        height: 16px;
        width: 16px;
        color: #fff;
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }

  &&& {
    &[disabled] {
      border-color: ${colors.gray.c300};
      pointer-events: none;

      &:checked {
        background-color: ${colors.gray.c200};

        &::before {
          background-color: ${props => props.color || otherColors.lxc.blue};
          background-image: none;
          mask-image: url(${Checkmark});
          mask-repeat: no-repeat;
          mask-position: 2px 3px;
          mask-size: 10px 8px;
        }
      }

      & + ${InputLabel} {
        pointer-events: none;
        color: ${colors.gray.c400};
      }
    }
  }
`;

const ErrorMessage = styled.div.attrs({
  className: 'h-3 text-xs text-red-500',
})``;

const StyledTooltip = styled(Tooltip)`
  & > .tooltip-inner {
    display: flex;
    justify-content: flex-start;
  }
`;
